import { Box, Tooltip } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import NirantaraIcon from "../assets/images/vajra.svg";
import { getConsultantIconUrl } from "../utils/consultantLogo";
import {
  mastersSidebarElements,
  miniSidebarElements,
  sidebarElements1,
  sidebarElementsFacility,
} from "../utils/constant";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SidebarItemsRenderer from "../utils/layoutFunctions";
import { useUser } from "../context/AuthContext";
import {
  filterMiniSidebarElements,
  getRoleBasedSidebar,
} from "../utils/sidebarFunctions";
import FeatureLockedModal from "../Components/Common/FeatureLockedModal";

const renderMainSidebarItems = (elements) => {
  return elements.map((item, index) => (
    <div
      key={index}
      style={{
        fontFamily: "Inter",
        display: "flex",
        flexDirection: "column",
        marginTop: "0.25rem",
      }}
    >
      <div
        key={index}
        style={{
          display: "flex",
          justifyContent: "center",
          backgroundColor:
            item.primary === "Masters" ? "#488257" : "transparent",
        }}
      >
        <Tooltip
          title={item.title}
          arrow
          placement="bottom-start"
          PopperProps={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 0],
                },
              },
            ],
          }}
          componentsProps={{
            tooltip: {
              sx: {
                color: "#fff",
                fontSize: "12px",
                borderRadius: "4px",
                padding: "0.25rem 0.5rem",
              },
              arrow: {
                color: "#fff",
              },
            },
          }}
        >
          <a
            href={item.path}
            key={item.primary}
            style={{
              textDecoration: "none",
              color: "green",
              padding: "0.4rem",
              borderRadius: "4px",
            }}
          >
            <Box
              component="img"
              src={item.icon}
              alt={"img"}
              sx={{
                width: "24px",
                height: "24px",
                filter: item.primary === "Masters" ? "invert(1)" : "invert(0)",
              }}
            />
          </a>
        </Tooltip>
      </div>
    </div>
  ));
};

const MastersSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const childRefs = useRef({});
  const { user } = useUser();

  const [isHovered, setIsHovered] = useState(false);
  const [featureLockedModal, setFeatureLockedModal] = useState(false);

  useEffect(() => {
    const sidebarEl = sidebarRef.current;
    if (!sidebarEl) return;

    const handleScroll = () => {
      localStorage.setItem("sidebarScrollTop", sidebarEl.scrollTop);
    };

    sidebarEl.addEventListener("scroll", handleScroll);

    return () => {
      sidebarEl.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const sidebarEl = sidebarRef.current;
    const savedScrollTop = localStorage.getItem("sidebarScrollTop");

    if (sidebarEl && savedScrollTop) {
      sidebarEl.scrollTop = parseInt(savedScrollTop, 10);
    }
  }, []);

  const sidebarElements = getRoleBasedSidebar({
    user,
    sidebarElements1,
    sidebarElementsFacility,
  });

  const filteredSidebar = sidebarElements.map((item) => {
    if (item.path === "/production-revenue") {
      return {
        ...item,
        heading: user?.companyAdmin ? "Production & Revenue" : "Production",
      };
    }
    if (item.heading === "Setup" && item.subData) {
      
      const newSubData = item.subData.filter(
        (sub) => sub.primary !== "Facility Master" || !user?.facility
      );
      return { ...item, subData: newSubData };
    }
    return item;
  });

  const miniSidebarFiltered = filterMiniSidebarElements({
    user,
    miniSidebarElements,
  });

  const [open, setOpen] = useState(() => {
    let activeHeader = null;
    let activeSub = null;

    for (const item of mastersSidebarElements) {
      for (const child of item.children || []) {
        if (child.path === location.pathname) {
          activeHeader = item.text;
          break;
        }
        for (const sub of child.subchildren || []) {
          if (sub.path === location.pathname) {
            activeHeader = item.text;
            activeSub = child.text;
            break;
          }
        }
      }
      if (activeHeader) break;
    }

    if (activeHeader) {
      const index = mastersSidebarElements.findIndex(
        (item) => item.text === activeHeader
      );
      return { index, header: activeHeader, sub: activeSub };
    }

    return {};
  });

  const handleClick = (item, index) => {
    setOpen((prevOpen) =>
      prevOpen.index === index ? {} : { index, header: item.text }
    );
  };

  const trialEnabledScope3Paths = [
    "/setup/masters/scope3/purchased-goods",
    "/setup/masters/scope3/transportation-distribution",
  ];

  // Trial users can expand Scope 3, but can only access selected modules.
  const isScope3Disabled = (item) => {
    if (!user?.isTrialUser) return false;
    if (item.text === "Scope 3") return false;
    if (!item.path?.includes("/scope3/")) return false;

    const isEnabledForTrial = trialEnabledScope3Paths.some(
      (path) => item.path === path || item.path.startsWith(`${path}/`)
    );

    return !isEnabledForTrial;
  };

  const renderSecondarySidebarItems = (elements, heading) => {
    return (
      <div style={{ width: "100%", fontFamily: "Inter", marginTop: "1.5rem" }}>
        {heading && (
          <p
            style={{
              color: "#fff",
              fontWeight: "700",
              fontSize: "24px",
              padding: "0 2rem 0 0.5rem",
              marginLeft: "1rem",
              borderBottom: "2px solid #fff",
              display: "inline-block",
            }}
          >
            {heading}
          </p>
        )}
        {elements.map((item, index) => {
          const isChildSelected =
            item.children &&
            item.children.some((child) => location.pathname === child.path);
          const isItemDisabled = isScope3Disabled(item);

          return item.heading ? (
            <p
              key={`heading-${index}`}
              style={{
                color: "rgba(255, 255, 255, 1)",
                paddingLeft: "1rem",
                marginBottom: "0.5rem",
                marginTop: "1rem",
                fontFamily: "Public Sans",
                fontSize: "16px",
                fontWeight: 400,
                lineHeight: "14px",
              }}
            >
              {item.heading}
            </p>
          ) : (
            <div key={`item-${index}`}>
              <Link
                to={isItemDisabled ? "#" : item.path}
                onClick={(e) => {
                  if (isItemDisabled) {
                    e.preventDefault();
                    setFeatureLockedModal(true);
                    return;
                  }
                  if (item.children) {
                    e.preventDefault();
                    handleClick(item, index);
                  } else {
                    // navigate(item.path);
                  }
                }}
                style={{
                  textDecoration: "none",
                  color: isItemDisabled
                    ? "rgba(255, 255, 255, 0.5)"
                    : location.pathname.startsWith(item?.path) || isChildSelected
                      ? "#488257"
                      : "#ffffff",
                  display: "flex",
                  padding: "0.5rem",
                  margin: "4px 8px",
                  backgroundColor: isItemDisabled
                    ? "transparent"
                    : location.pathname.startsWith(item?.path) || isChildSelected
                      ? "#ffffff"
                      : "transparent",
                  borderRadius: "4px",
                  alignItems: "center",
                  cursor: isItemDisabled ? "not-allowed" : "pointer",
                  opacity: isItemDisabled ? 0.5 : 1,
                }}
              >
                <Box
                  component="img"
                  src={item.icon}
                  alt={"img"}
                  sx={{
                    filter: isItemDisabled
                      ? "opacity(0.5)"
                      : location.pathname.startsWith(item?.path)
                        ? "invert(32%) sepia(23%) saturate(900%) hue-rotate(90deg) brightness(70%) contrast(100%)"
                        : "invert(0)",
                  }}
                />
                <span
                  style={{
                    marginLeft: "1rem",
                    fontSize: "16px",
                    fontWeight: "400",
                    color: "inherit",
                  }}
                >
                  {item.text}
                </span>
                {item.children && (
                  <span style={{ marginLeft: "auto", fontSize: "16px" }}>
                    {open?.header === item.text ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </span>
                )}
              </Link>
              {item.children && open?.header === item.text && (
                <div style={{ paddingLeft: "0.5rem" }}>
                  {item.children.map((child, childIndex) => {
                    const isActive = location.pathname === child.path;
                    const isSubMenuOpen = open?.sub === child.text;
                    const isChildDisabled = isScope3Disabled(child);

                    // Refs
                    if (!childRefs.current[child.path]) {
                      childRefs.current[child.path] = React.createRef();
                    }

                    return (
                      <div key={`child-${childIndex}`}>
                        <Link
                          to={isChildDisabled ? "#" : child.path}
                          ref={childRefs.current[child.path]}
                          onClick={(e) => {
                            if (isChildDisabled) {
                              e.preventDefault();
                              setFeatureLockedModal(true);
                              return;
                            }
                            if (child.subchildren) {
                              e.preventDefault();
                              setOpen((prev) =>
                                prev?.sub === child.text
                                  ? { ...prev, sub: null }
                                  : { ...prev, sub: child.text }
                              );
                            }
                          }}
                          style={{
                            textDecoration: "none",
                            color: isChildDisabled
                              ? "rgba(255, 255, 255, 0.5)"
                              : "#ffffff",
                            display: "flex",
                            padding: "0.5rem 0 0.5rem 0.5rem",
                            margin: "4px 8px",
                            borderRadius: "4px",
                            alignItems: "center",
                            cursor: isChildDisabled ? "not-allowed" : "pointer",
                            backgroundColor: isChildDisabled
                              ? "transparent"
                              : isActive
                                ? "#ffffff33"
                                : "transparent",
                            opacity: isChildDisabled ? 0.5 : 1,
                          }}
                        >
                          {!child.subchildren &&
                            (isActive ? (
                              <CheckCircleOutlineIcon
                                style={{ width: "16px" }}
                              />
                            ) : (
                              <RadioButtonUncheckedIcon
                                style={{ width: "16px" }}
                              />
                            ))}

                          <span
                            style={{
                              marginLeft: "0.5rem",
                              fontSize: "16px",
                              fontWeight: "400",
                              color: "inherit",
                            }}
                          >
                            {child.text}
                          </span>
                          {child.subchildren && (
                            <span
                              style={{ marginLeft: "auto", fontSize: "16px" }}
                            >
                              {isSubMenuOpen ? <ExpandLess /> : <ExpandMore />}
                            </span>
                          )}
                        </Link>

                        {/* Render subchildren dropdown */}
                        {child.subchildren && isSubMenuOpen && (
                          <div style={{ paddingLeft: "0.5rem" }}>
                            {child.subchildren.map((sub, subIndex) => {
                              const isSubActive =
                                location.pathname === sub.path;
                              const isSubDisabled = isScope3Disabled(sub);
                              return (
                                <Link
                                  key={`sub-${subIndex}`}
                                  to={isSubDisabled ? "#" : sub.path}
                                  onClick={(e) => {
                                    if (isSubDisabled) {
                                      e.preventDefault();
                                      setFeatureLockedModal(true);
                                    }
                                  }}
                                  style={{
                                    textDecoration: "none",
                                    color: isSubDisabled
                                      ? "rgba(255, 255, 255, 0.5)"
                                      : "#ffffff",
                                    display: "flex",
                                    padding: "0.25rem 0 0.5rem 1rem",
                                    margin: "4px 8px",
                                    borderRadius: "4px",
                                    alignItems: "center",
                                    cursor: isSubDisabled ? "not-allowed" : "pointer",
                                    backgroundColor: isSubDisabled
                                      ? "transparent"
                                      : isSubActive
                                        ? "#ffffff22"
                                        : "transparent",
                                    opacity: isSubDisabled ? 0.5 : 1,
                                  }}
                                >
                                  {isSubActive ? (
                                    <CheckCircleOutlineIcon
                                      style={{ width: "14px" }}
                                    />
                                  ) : (
                                    <RadioButtonUncheckedIcon
                                      style={{ width: "14px" }}
                                    />
                                  )}
                                  <span
                                    style={{
                                      marginLeft: "0.5rem",
                                      fontSize: "15px",
                                      fontWeight: "400",
                                      color: "inherit",
                                    }}
                                  >
                                    {sub.text}
                                  </span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Box style={{ display: "flex" }}>
        <div
          style={{
            width: "22.5%",
            height: "100vh",
            backgroundColor: "#E6E6E6",
            position: "relative",
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Box display="flex" justifyContent="center" p={2}>
            <img
              src={getConsultantIconUrl() || NirantaraIcon}
              alt="Nirantara Logo"
              style={
                getConsultantIconUrl()
                  ? { cursor: "pointer", height: "36px", width: "36px", objectFit: "contain" }
                  : { cursor: "pointer", height: "44px", width: "auto", objectFit: "contain" }
              }
              onClick={() => navigate("/dashboard")}
            />
          </Box>
          <div style={{ width: "100%" }}>
            {renderMainSidebarItems(miniSidebarFiltered)}
          </div>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "21.3vw",
              height: "100vh",
              backgroundColor: "#fff",
              zIndex: 10,
              overflowY: "auto",
              transform: isHovered ? "translateX(0)" : "translateX(-100%)",
              opacity: isHovered ? 1 : 0,
              transition: "opacity 0.4s ease, transform 0.4s ease",
              pointerEvents: isHovered ? "auto" : "none",
              willChange: "transform, opacity",
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {<SidebarItemsRenderer elements={filteredSidebar} />}
          </div>
        </div>
        <div
          ref={sidebarRef}
          style={{
            backgroundColor: "rgba(72, 130, 87, 1)",
            width: "77.5%",
            overflowY: "auto",
            maxHeight: "100vh",
          }}
        >
          {renderSecondarySidebarItems(mastersSidebarElements, "Masters")}
        </div>
      </Box>
      {user?.isTrialUser && (
        <FeatureLockedModal
          open={featureLockedModal}
          onClose={() => setFeatureLockedModal(false)}
        />
      )}
    </>
  );
};

export default MastersSidebar;
